import { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import {
  Shield,
  UserPlus,
  UserMinus,
  Clock,
  Mail,
  ChevronDown,
  ChevronUp,
  AlertCircle,
} from "lucide-react";
import { friendlyError } from "../utils/errors";
import {
  fetchDealAccess,
  grantAccess,
  revokeAccess,
  fetchAccessLogs,
  DealAccessRecord,
  AccessLogEntry,
} from "../api/client";

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

export default function AdminPage() {
  const { dealId } = useParams<{ dealId: string }>();
  const [accessRecords, setAccessRecords] = useState<DealAccessRecord[]>([]);
  const [logs, setLogs] = useState<AccessLogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showInvite, setShowInvite] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState("analyst");
  const [inviting, setInviting] = useState(false);
  const [showLogs, setShowLogs] = useState(false);
  const [confirmRevokeId, setConfirmRevokeId] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    if (!dealId) return;
    try {
      setLoading(true);
      const [acc, lg] = await Promise.all([
        fetchDealAccess(dealId),
        fetchAccessLogs(dealId),
      ]);
      setAccessRecords(acc);
      setLogs(lg);
      setError(null);
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : "Failed to load admin data";
      setError(friendlyError(msg));
    } finally {
      setLoading(false);
    }
  }, [dealId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!dealId || !inviteEmail.trim()) return;
    setInviting(true);
    try {
      await grantAccess(dealId, { email: inviteEmail.trim(), role: inviteRole });
      setInviteEmail("");
      setShowInvite(false);
      await loadData();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Failed to grant access";
      setError(friendlyError(msg));
    } finally {
      setInviting(false);
    }
  };

  const handleRevoke = async (accessId: string) => {
    if (!dealId) return;
    try {
      await revokeAccess(dealId, accessId);
      setConfirmRevokeId(null);
      await loadData();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Failed to revoke access";
      setError(friendlyError(msg));
    }
  };

  const activeAccess = accessRecords.filter((r) => !r.revoked_at);
  const revokedAccess = accessRecords.filter((r) => r.revoked_at);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-2 border-grove-300 border-t-forest-700 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fade-in">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-forest-50 border border-forest-200 flex items-center justify-center">
            <Shield className="w-5 h-5 text-forest-600" />
          </div>
          <div>
            <h2 className="font-display text-xl font-semibold text-bark-800">
              Deal Administration
            </h2>
            <p className="text-sm text-bark-400 mt-0.5">
              Manage access permissions and view activity logs
            </p>
          </div>
        </div>
        <button
          onClick={() => setShowInvite(!showInvite)}
          className="flex items-center gap-2 px-5 py-2.5 bg-honey-600 hover:bg-honey-500 text-white font-semibold text-sm rounded-lg transition-all shadow-md hover:shadow-lg"
        >
          <UserPlus className="w-4 h-4" />
          Grant Access
        </button>
      </div>

      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-xl flex items-center gap-3 text-red-700 text-sm">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          {error}
        </div>
      )}

      {/* Invite form */}
      {showInvite && (
        <div className="p-6 bg-white border border-grove-300 rounded-xl shadow-sm animate-slide-in">
          <h3 className="text-sm font-semibold text-bark-700 mb-4">
            Invite User
          </h3>
          <form onSubmit={handleInvite} className="flex items-end gap-4">
            <div className="flex-1">
              <label className="block text-xs font-medium text-bark-400 mb-2">
                Email Address
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-bark-300" />
                <input
                  type="email"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  placeholder="analyst@firm.com"
                  required
                  className="w-full pl-10 pr-4 py-2.5 bg-white border border-grove-300 rounded-lg text-sm text-bark-700 placeholder-bark-300"
                />
              </div>
            </div>
            <div className="w-40">
              <label className="block text-xs font-medium text-bark-400 mb-2">Role</label>
              <select
                value={inviteRole}
                onChange={(e) => setInviteRole(e.target.value)}
                className="w-full px-4 py-2.5 bg-white border border-grove-300 rounded-lg text-sm text-bark-700"
              >
                <option value="analyst">Analyst</option>
                <option value="broker">Broker</option>
              </select>
            </div>
            <button
              type="submit"
              disabled={inviting}
              className="px-6 py-2.5 bg-honey-600 hover:bg-honey-500 disabled:opacity-50 text-white font-semibold text-sm rounded-lg transition-all shadow-sm hover:shadow-md"
            >
              {inviting ? "Granting..." : "Grant"}
            </button>
          </form>
        </div>
      )}

      {/* Active access */}
      <div>
        <h3 className="text-sm font-semibold text-bark-500 uppercase tracking-wider mb-4">
          Active Access ({activeAccess.length})
        </h3>
        <div className="bg-white border border-grove-300 rounded-xl overflow-hidden shadow-sm">
          {activeAccess.length === 0 ? (
            <p className="p-8 text-sm text-bark-400 text-center">
              No users have access to this deal. Click "Grant Access" above to invite someone.
            </p>
          ) : (
            <table className="w-full">
              <thead>
                <tr className="border-b border-grove-200 bg-grove-50">
                  <th className="text-left px-6 py-3 text-xs font-semibold text-bark-400 uppercase tracking-wider">
                    User
                  </th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-bark-400 uppercase tracking-wider">
                    Role
                  </th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-bark-400 uppercase tracking-wider">
                    Granted
                  </th>
                  <th className="text-right px-6 py-3 text-xs font-semibold text-bark-400 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {activeAccess.map((rec) => (
                  <tr
                    key={rec.id}
                    className="border-b border-grove-200 last:border-0 hover:bg-grove-50 transition-colors"
                  >
                    <td className="px-6 py-4 text-sm text-bark-700">
                      {rec.user_email || rec.user_id}
                    </td>
                    <td className="px-6 py-4">
                      <span
                        className={`inline-block px-3 py-0.5 text-xs font-medium rounded-full ${
                          rec.role === "broker"
                            ? "bg-honey-100 text-honey-700 border border-honey-200"
                            : "bg-grove-100 text-bark-500 border border-grove-300"
                        }`}
                      >
                        {rec.role}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-bark-400">
                      {formatDate(rec.granted_at)}
                    </td>
                    <td className="px-6 py-4 text-right">
                      {confirmRevokeId === rec.id ? (
                        <button
                          onClick={() => handleRevoke(rec.id)}
                          onBlur={() => setTimeout(() => setConfirmRevokeId(null), 2000)}
                          autoFocus
                          className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-semibold text-white bg-red-600 rounded-lg hover:bg-red-700 transition-all"
                        >
                          Confirm Revoke
                        </button>
                      ) : (
                        <button
                          onClick={() => setConfirmRevokeId(rec.id)}
                          className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-all"
                        >
                          <UserMinus className="w-3.5 h-3.5" />
                          Revoke
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Revoked access */}
      {revokedAccess.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-bark-500 uppercase tracking-wider mb-4">
            Revoked Access ({revokedAccess.length})
          </h3>
          <div className="bg-white border border-grove-300 rounded-xl overflow-hidden shadow-sm">
            <table className="w-full">
              <thead>
                <tr className="border-b border-grove-200 bg-grove-50">
                  <th className="text-left px-6 py-3 text-xs font-semibold text-bark-400 uppercase tracking-wider">
                    User
                  </th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-bark-400 uppercase tracking-wider">
                    Role
                  </th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-bark-400 uppercase tracking-wider">
                    Granted
                  </th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-bark-400 uppercase tracking-wider">
                    Revoked
                  </th>
                </tr>
              </thead>
              <tbody>
                {revokedAccess.map((rec) => (
                  <tr
                    key={rec.id}
                    className="border-b border-grove-200 last:border-0 opacity-60"
                  >
                    <td className="px-6 py-3.5 text-sm text-bark-500">
                      {rec.user_email || rec.user_id}
                    </td>
                    <td className="px-6 py-3.5 text-xs text-bark-400">
                      {rec.role}
                    </td>
                    <td className="px-6 py-3.5 text-sm text-bark-400">
                      {formatDate(rec.granted_at)}
                    </td>
                    <td className="px-6 py-3.5 text-sm text-red-500">
                      {rec.revoked_at ? formatDate(rec.revoked_at) : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Access logs */}
      <div>
        <button
          onClick={() => setShowLogs(!showLogs)}
          className="flex items-center gap-2 text-sm font-semibold text-bark-500 uppercase tracking-wider mb-4 hover:text-bark-700 transition-colors"
        >
          <Clock className="w-4 h-4" />
          Activity Log ({logs.length})
          {showLogs ? (
            <ChevronUp className="w-4 h-4" />
          ) : (
            <ChevronDown className="w-4 h-4" />
          )}
        </button>

        {showLogs && (
          <div className="bg-white border border-grove-300 rounded-xl overflow-hidden shadow-sm animate-slide-in">
            {logs.length === 0 ? (
              <p className="p-8 text-sm text-bark-400 text-center">
                No activity recorded yet.
              </p>
            ) : (
              <div className="max-h-96 overflow-y-auto">
                <table className="w-full">
                  <thead className="sticky top-0 bg-grove-50">
                    <tr className="border-b border-grove-200">
                      <th className="text-left px-6 py-3 text-xs font-semibold text-bark-400 uppercase tracking-wider">
                        Time
                      </th>
                      <th className="text-left px-6 py-3 text-xs font-semibold text-bark-400 uppercase tracking-wider">
                        User
                      </th>
                      <th className="text-left px-6 py-3 text-xs font-semibold text-bark-400 uppercase tracking-wider">
                        Action
                      </th>
                      <th className="text-left px-6 py-3 text-xs font-semibold text-bark-400 uppercase tracking-wider">
                        Detail
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {logs.map((log) => (
                      <tr
                        key={log.id}
                        className="border-b border-grove-200 last:border-0 hover:bg-grove-50 transition-colors"
                      >
                        <td className="px-6 py-3 text-xs text-bark-400 whitespace-nowrap font-mono">
                          {formatDate(log.timestamp)}
                        </td>
                        <td className="px-6 py-3 text-sm text-bark-600">
                          {log.user_email || log.user_id}
                        </td>
                        <td className="px-6 py-3">
                          <span className="inline-block px-2.5 py-0.5 text-xs font-medium bg-grove-100 text-bark-500 rounded-md border border-grove-200">
                            {log.action}
                          </span>
                        </td>
                        <td className="px-6 py-3 text-sm text-bark-400">
                          {log.detail || "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
